import {
  useEffect,
  useRef,
  useState,
  type ElementType,
  type ReactNode,
  type CSSProperties,
} from "react";

type RevealVariant = "up" | "fade" | "left" | "right" | "scale";

interface RevealProps {
  children: ReactNode;
  /** Animation style. Defaults to "up". */
  variant?: RevealVariant;
  /** Delay in ms before the element animates in (use for staggering). */
  delay?: number;
  /** Render as a different element (e.g. "li", "button", "section"). */
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
  /** Re-trigger every time it scrolls into view (default reveals once). */
  repeat?: boolean;
  /** Any extra props (onClick, type, aria-*, etc.) are forwarded to the element. */
  [key: string]: unknown;
}

export function Reveal({
  children,
  variant = "up",
  delay = 0,
  as,
  className,
  style,
  repeat = false,
  ...rest
}: RevealProps) {
  const Tag = (as ?? "div") as ElementType;
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (!repeat) observer.disconnect();
        } else if (repeat) {
          setVisible(false);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [repeat]);

  return (
    <Tag
      ref={ref}
      data-reveal={variant}
      className={`${visible ? "is-visible" : ""}${className ? ` ${className}` : ""}`}
      style={{ ...style, ["--reveal-delay" as string]: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
